import { cleanDescription, formatDate, parseSeomaticDescription, generatePageId } from '~/utils/common';

export const generateSchema = (entry, seomatic = {}, locale = null, siteIdentity = {}) => {
  const { $localeRootPath } = useNuxtApp();
  const baseUrl = siteIdentity.url || 'https://blackroll.com';
  const pageId = generatePageId(entry.uri, $localeRootPath);

  // Check if schema.org is enabled and get the selected type
  if (!entry.schemaOrg?.enableSchemaOrg) {
    return null; // Don't generate schema if disabled
  }

  // Use the selected schema type from the new Schema.org tab
  // Default to BlogPosting if no type is specified
  const schemaType = entry.schemaOrg?.schemaOrgType || 'BlogPosting';

  // Use seomatic metaTagContainer for a richer description if available
  const description = parseSeomaticDescription(seomatic, entry.heading);

  // Generate expert author schema if expert is associated
  let author = undefined;
  if (entry.expert?.[0]) {
    const expert = entry.expert[0];
    const expertPageId = `${baseUrl}${$localeRootPath('/' + expert.uri)}`;

    author = {
      '@type': 'Person',
      '@id': expertPageId,
      name: expert.title,
      description: expert.description || expert.heading,
      url: expertPageId,
      image: expert.image?.[0]?.url,
      sameAs: expert.socialLinks || [],
      jobTitle: expert.jobTitle,
      worksFor: {
        '@type': 'Organization',
        name: siteIdentity.name || 'BLACKROLL',
      },
    };
  }

  // Base schema structure
  const schema = {
    '@context': 'https://schema.org',
    '@type': schemaType,
    '@id': pageId,
    headline: entry.heading,
    description: cleanDescription(description),
    datePublished: formatDate(entry.postDate),
    dateModified: formatDate(entry.dateUpdated),
    author: author,
    publisher: {
      '@type': 'Organization',
      name: siteIdentity.name || 'BLACKROLL',
      logo: siteIdentity.logo
        ? {
            '@type': 'ImageObject',
            url: siteIdentity.logo,
          }
        : undefined,
    },
  };

  // Add schema-specific properties based on type
  if (schemaType === 'BlogPosting') {
    // BlogPosting specific properties
    if (entry.categories?.[0]?.title) {
      schema.articleSection = entry.categories[0].title;
    } else if (entry.topics?.[0]?.title) {
      schema.articleSection = entry.topics[0].title;
    }
  } else if (schemaType === 'Article') {
    // Article specific properties
    if (entry.topics?.[0]?.title) {
      schema.articleSection = entry.topics[0].title;
    }
  } else if (schemaType === 'WebPage') {
    // WebPage specific properties
    schema.name = entry.heading;
    schema.description = cleanDescription(description);
  } else if (schemaType === 'Product') {
    // Product specific properties (if needed for articles)
    schema.name = entry.heading;
    schema.description = cleanDescription(description);
    // Note: Product schema for articles might need additional product-specific fields
  }

  // Add image if available
  if (entry.image?.[0]?.url) {
    schema.image = entry.image[0].url;
  }

  // Add additional properties for better SEO
  if (entry.contentBlocks?.length > 0) {
    schema.articleBody = entry.contentBlocks
      .map((block) => block.text || block.content || '')
      .filter((text) => text.trim())
      .join(' ')
      .substring(0, 500); // Limit to 500 characters
  }

  // Add keywords if available
  if (entry.tags?.length > 0) {
    schema.keywords = entry.tags.join(', ');
  }

  // Add language
  if (locale) {
    schema.inLanguage = locale;
  }

  return schema;
};
